// src/components/common/taskbox/EditTaskModal.js
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

const PRIORITY_OPTIONS = [
    { value: 'immediate', label: 'Immediate', color: 'red' },
    { value: 'urgent', label: 'Urgent', color: 'orange' },
    { value: 'next_in_line', label: 'Next in Line', color: 'yellow' },
    { value: 'scheduled', label: 'Scheduled', color: 'green' },
    { value: 'deferred', label: 'Deferred', color: 'gray' }
];

const TASK_TYPES = [
    { value: 'daily', label: 'Daily Task' },
    { value: 'weekly', label: 'Weekly Task' },
    { value: 'monthly', label: 'Monthly Task' },
    { value: 'quarterly', label: 'Quarterly Task' },
    { value: 'annual', label: 'Annual Task' }
];

const CATEGORIES = [
    { value: 'work', label: 'Work' },
    { value: 'personal', label: 'Personal' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'project', label: 'Project' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'review', label: 'Review' },
    { value: 'other', label: 'Other' }
];

const EditTaskModal = ({ task, onClose, onSave, theme }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        task_type: 'daily',
        priority: 'scheduled',
        deadline: '',
        category: 'work'
    });
    const [checklist, setChecklist] = useState([]);
    const [newChecklistItem, setNewChecklistItem] = useState('');

    useEffect(() => {
        if (task) {
            setFormData({
                title: task.title || '',
                description: task.description || '',
                task_type: task.task_type || 'daily',
                priority: task.priority || 'scheduled',
                deadline: task.deadline ? task.deadline.split('T')[0] : '',
                category: task.category || 'work'
            });
            setChecklist(task.checklist || []);
        }
    }, [task]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddChecklistItem = () => {
        if (newChecklistItem.trim()) {
            setChecklist([...checklist, { title: newChecklistItem, is_completed: false, isNew: true }]);
            setNewChecklistItem('');
        }
    };

    const handleRemoveChecklistItem = (index) => {
        setChecklist(checklist.filter((_, i) => i !== index));
    };

    const handleToggleChecklistItem = (index) => {
        const updated = [...checklist];
        updated[index].is_completed = !updated[index].is_completed;
        setChecklist(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            alert('Please enter a task title');
            return;
        }

        setLoading(true);
        try {
            const taskData = {
                ...formData,
                // Convert empty strings to null for date fields
                deadline: formData.deadline || null,
                // Convert empty strings to null for text fields
                description: formData.description || null,
                category: formData.category || null,
                // Include checklist
                checklist: checklist
            };

            await onSave(task.id, taskData);
        } catch (error) {
            console.error('Error saving task:', error);
        } finally {
            setLoading(false);
        }
    };

    const getPriorityColorClass = (priority, isButton = false) => {
        const colors = {
            immediate: isButton ? 'border-red-600 text-red-600' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
            urgent: isButton ? 'border-orange-600 text-orange-600' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
            next_in_line: isButton ? 'border-yellow-600 text-yellow-600' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
            scheduled: isButton ? 'border-green-600 text-green-600' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
            deferred: isButton ? 'border-gray-600 text-gray-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
        };
        return colors[priority] || colors.scheduled;
    };

    if (!task) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className={`text-xl font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Edit Task
                    </h2>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Task Title <span className="text-red-600">*</span>
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Enter task title..."
                            required
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Add task description..."
                            rows="3"
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                    </div>

                    {/* Task Type and Category */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Task Type
                            </label>
                            <select
                                name="task_type"
                                value={formData.task_type}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            >
                                {TASK_TYPES.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Category
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className={`w-full px-4 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            >
                                {CATEGORIES.map(cat => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Priority */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Priority
                        </label>
                        <div className="grid grid-cols-5 gap-2">
                            {PRIORITY_OPTIONS.map(priority => (
                                <button
                                    key={priority.value}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, priority: priority.value }))}
                                    className={`px-3 py-2 rounded-lg border-2 font-medium text-sm transition-all ${
                                        formData.priority === priority.value
                                            ? getPriorityColorClass(priority.value, true) + ' bg-opacity-10'
                                            : theme === 'dark'
                                                ? 'border-gray-600 text-gray-400 hover:border-gray-500'
                                                : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                    }`}
                                >
                                    {priority.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Deadline */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Deadline
                        </label>
                        <input
                            type="date"
                            name="deadline"
                            value={formData.deadline}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                    </div>

                    {/* Checklist */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Checklist
                        </label>

                        {/* Existing Checklist Items */}
                        {checklist.length > 0 && (
                            <div className="space-y-2 mb-3">
                                {checklist.map((item, index) => (
                                    <div
                                        key={index}
                                        className={`flex items-center gap-2 p-2 rounded-lg ${
                                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={item.is_completed}
                                            onChange={() => handleToggleChecklistItem(index)}
                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className={`flex-1 ${
                                            item.is_completed
                                                ? `line-through ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`
                                                : theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {item.title}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveChecklistItem(index)}
                                            className={`p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 transition-colors`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add New Checklist Item */}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newChecklistItem}
                                onChange={(e) => setNewChecklistItem(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddChecklistItem();
                                    }
                                }}
                                placeholder="Add checklist item..."
                                className={`flex-1 px-3 py-2 rounded-lg border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                            />
                            <button
                                type="button"
                                onClick={handleAddChecklistItem}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className={`flex gap-3 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditTaskModal;
